import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store from '../../store/store';
import { toast } from 'react-toastify';
import {
    Typography,
    Autocomplete,
    TextField,
    Box,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    MenuItem,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import YearPicker from '../Masking/YearPickerNormal';
import {
    setProductivityIndex,
    setProductivityLandCover,
    setProductivityYears,
    setLandCoverIndex,
    setLandCoverYears,
    setSoilOrganicIndex,
    setSoilOrganicLandCoverIndex,
    setSoilOrganicYears,
    setProductivityDecimals,
    setProductivityAdvanced,
    setTransformationMatrix,
    setAdvancedEnabled,
    setLandCoverAdvanced,
    setLandCoverTransformationMatrix,
    setLandCoverAdvancedEnabled,
    setSoilOrganicAdvanced,
    setShowSoilOrganicLandCoverAdvanced,
    setSoilOrganicTransformationMatrix,
    setLandCoverIndicatorMatrix,
    setSoilOrganicLandCoverTransformationMatrix,
    setSoilOrganicAdvancedEnabled,
    setProgressAdvancedEnabled,
    setProgressLandCoverAdvancedEnabled,
    setProgressSoilOrganicAdvancedEnabled,
    setSoilOrganicLandCoverAdvanced,
    setSoilOrganicLandCoverAdvancedEnabled,
    setProgressSoilOrganicLandCoverAdvancedEnabled
} from '../../reducers/LandDegradationSlice';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoIcon from '@mui/icons-material/Info';

const MatrixRowInput = ({ rowKey, value, onMatrixChange }) => {
    const [inputValue, setInputValue] = useState(value.join(', '));

    useEffect(() => {
        setInputValue(value.join(', '));
    }, [value]);

    const handleChange = (e) => {
        const newValue = e.target.value;
        if (/^[\d,\s]*$/.test(newValue)) {
            setInputValue(newValue);
        }
    };

    const handleBlur = () => {
        const parsedValues = inputValue
            .split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        onMatrixChange(rowKey, parsedValues);
        setInputValue(parsedValues.join(', '));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    return (
        <TextField
            fullWidth
            size="small"
            value={inputValue}
            placeholder="e.g., 1, 2, 3, 4"
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
            helperText={`${value.length}/20 values`}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#339ace',
                        }
                    }
                }
            }}
        />
    );
};


const BaselineConfig = ({ config, setConfig }) => {
    const dispatch = useDispatch();
    const productivityIndex = useSelector((state) => state.landDegradation.productivityIndex);
    const landCoverIndex = useSelector((state) => state.landDegradation.landCoverIndex);
    const soilOrganicIndex = useSelector((state) => state.landDegradation.soilOrganicIndex);
    const soilOrganicLandCoverIndex = useSelector((state) => state.landDegradation.soilOrganicLandCoverIndex);
    const productivityLandCover = useSelector((state) => state.landDegradation.productivityLandCover);

    const productivityYearFrom = useSelector((state) => state.landDegradation.productivityYears?.from);
    const productivityYearTo = useSelector((state) => state.landDegradation.productivityYears?.to);
    const landCoverYearFrom = useSelector((state) => state.landDegradation.landCoverYears?.from);
    const landCoverYearTo = useSelector((state) => state.landDegradation.landCoverYears?.to);
    const soilOrganicYearFrom = useSelector((state) => state.landDegradation.soilOrganicYears?.from);
    const soilOrganicYearTo = useSelector((state) => state.landDegradation.soilOrganicYears?.to);

    const [showLandCoverIndicatorMatrix, setShowLandCoverIndicatorMatrix] = useState(false);
    const landCoverIndicatorMatrix = useSelector((state) => state.landDegradation.landCoverIndicatorMatrix);

    const initialIndicatorMatrix = {
        treeCovered: [4, 1, 1, 1, 1, 1, 4],
        grassland: [5, 4, 5, 1, 1, 1, 4],
        cropland: [5, 1, 4, 1, 1, 1, 4],
        wetland: [1, 1, 1, 4, 1, 1, 4],
        artificialArea: [5, 5, 5, 5, 4, 5, 4],
        bareLand: [5, 5, 5, 5, 1, 4, 4],
        water: [4, 4, 4, 4, 4, 4, 4]
    };

    // Copy From Dialog States
    const [copyFromDialogOpen, setCopyFromDialogOpen] = useState(false);
    const [copySource, setCopySource] = useState('');
    const [currentMatrixType, setCurrentMatrixType] = useState(''); // 'productivity', 'landcover', 'soilorganic'

    const getTransitionColor = (value) => {
        switch (value) {
            case "5": return "#4CAF50";
            case "1": return "#9C27B0";
            case "4": return "#FFFFFF";
            default: return "#FFFFFF";
        }
    };

    const handleResetTable = () => {
        dispatch(setLandCoverIndicatorMatrix(initialIndicatorMatrix));
    };

    // Validation function for transformation matrix
    const validateTransformationMatrix = (matrix) => {
        if (!matrix) return { valid: false, error: "Matrix is empty" };

        const allValues = Object.values(matrix).flat();
        if (allValues.length === 0) {
            return { valid: false, error: "All fields cannot be empty" };
        }
        if (allValues.length > 20) {
            return { valid: false, error: "Maximum 20 values allowed" };
        }

        return { valid: true };
    };

    // Copy matrix from other indicators
    const handleOpenCopyDialog = (matrixType) => {
        setCurrentMatrixType(matrixType);
        setCopyFromDialogOpen(true);
        setCopySource('');
    };

    const handleCopyMatrix = () => {
        if (!copySource) {
            toast.error("Please select a source");
            return;
        }

        const state = store.getState().landDegradation;
        let sourceMatrix = null;

        switch (copySource) {
            case 'baseline-productivity':
                sourceMatrix = state.productivityAdvanced.transformationMatrix;
                break;
            case 'baseline-landcover':
                sourceMatrix = state.landCoverAdvanced.transformationMatrix;
                break;
            case 'baseline-soilorganic':
                sourceMatrix = state.soilOrganicLandCoverAdvanced.transformationMatrix;
                break;
            case 'progress-productivity':
                sourceMatrix = state.progressProductivityAdvanced.transformationMatrix;
                break;
            case 'progress-landcover':
                sourceMatrix = state.progressLandCoverAdvanced.transformationMatrix;
                break;
            case 'progress-soilorganic':
                sourceMatrix = state.progressSoilOrganicLandCoverAdvanced.transformationMatrix;
                break;
        }

        if (sourceMatrix) {
            // Apply to current indicator's matrix based on type
            switch (currentMatrixType) {
                case 'productivity':
                    dispatch(setTransformationMatrix(sourceMatrix));
                    break;
                case 'landcover':
                    dispatch(setLandCoverTransformationMatrix(sourceMatrix));
                    break;
                case 'soilorganic':
                    dispatch(setSoilOrganicLandCoverTransformationMatrix(sourceMatrix));
                    break;
            }
            toast.success("Matrix copied successfully");
        } else {
            toast.error("Source matrix not found or is empty");
        }
        setCopyFromDialogOpen(false);
    };

    const handleClearMatrix = (matrixType) => {
        const emptyMatrix = {
            treeCovered: [],
            grassland: [],
            cropland: [],
            wetland: [],
            artificialArea: [],
            bareLand: [],
            water: []
        };

        switch (matrixType) {
            case 'productivity':
                dispatch(setTransformationMatrix(emptyMatrix));
                break;
            case 'landcover':
                dispatch(setLandCoverTransformationMatrix(emptyMatrix));
                break;
            case 'soilorganic':
                dispatch(setSoilOrganicLandCoverTransformationMatrix(emptyMatrix));
                break;
        }
        toast.info("Matrix cleared");
    };

    const getIndexAvailableFrom = (indexId) => {
        try {
            const categoriesStr = localStorage.getItem('categories');
            if (!categoriesStr) return 2000;

            const categories = JSON.parse(categoriesStr);
            const selectedIndex = categories.find(category => category.id === indexId);

            if (selectedIndex && selectedIndex.available_from) {
                const fromDate = new Date(selectedIndex.available_from);
                return fromDate.getFullYear();
            }
            return 2000;
        } catch (error) {
            console.error('Error getting available_from:', error);
            return 2000;
        }
    };

    const getIndexAvailableTo = (indexId) => {
        try {
            const categoriesStr = localStorage.getItem('categories');
            if (!categoriesStr) return 2022;

            const categories = JSON.parse(categoriesStr);
            const selectedIndex = categories.find(category => category.id === indexId);

            if (selectedIndex && selectedIndex.available_to) {
                const toDate = new Date(selectedIndex.available_to);
                return toDate.getFullYear();
            }
            return 2022;
        } catch (error) {
            console.error('Error getting available_to:', error);
            return 2022;
        }
    };

    const handleMatrixCellChange = (rowKey, colIndex, value) => {
        const newMatrix = landCoverIndicatorMatrix ? {
            ...landCoverIndicatorMatrix,
            [rowKey]: landCoverIndicatorMatrix[rowKey].map(
                (v, i) => i === colIndex ? parseInt(value) : v
            )
        } : {
            ...initialIndicatorMatrix,
            [rowKey]: initialIndicatorMatrix[rowKey].map(
                (v, i) => i === colIndex ? parseInt(value) : v
            )
        };
        dispatch(setLandCoverIndicatorMatrix(newMatrix));
    };

    // Get allIndexes from localStorage - memoized for performance
    const allIndexes = React.useMemo(() => {
        try {
            const categoriesStr = localStorage.getItem('categories');
            if (!categoriesStr) return [{ id: 'ndvi', name: 'MODIS Yearly NDVI', key: 'ndvi' }];
            const categories = JSON.parse(categoriesStr);
            return categories.map(category => ({
                id: category.id,
                name: category.name,
                key: category.id,
                type: category.type
            }));
        } catch (error) {
            console.error('Error parsing categories:', error);
            return [{ id: 'ndvi', name: 'MODIS Yearly NDVI', key: 'ndvi' }];
        }
    }, []);

    const landCoverIndexes = React.useMemo(() =>
        allIndexes.filter(index =>
            index.name.toLowerCase().includes('landcover') ||
            index.name.toLowerCase().includes('land cover') ||
            index.type === 'landcover'
        ), [allIndexes]
    );

    // Initialize default indexes - runs only ONCE on mount
    useEffect(() => {
        console.log('[BaselineConfig] Initializing default indexes...');

        // Set default productivity index if not set
        if (!productivityIndex && allIndexes.length > 0) {
            const defaultIndex = allIndexes.find(index =>
                index.name.toLowerCase().includes("yearly ndvi")
            ) || allIndexes[0];
            console.log('[BaselineConfig] Setting default productivity index:', defaultIndex.id);
            dispatch(setProductivityIndex(defaultIndex.id));
        }

        // Set default land cover indexes if not set
        if (!landCoverIndex && landCoverIndexes.length > 0) {
            const defaultLandCover = landCoverIndexes.find(index =>
                index.name.toLowerCase().includes('esa cci')
            ) || landCoverIndexes[0];
            console.log('[BaselineConfig] Setting default land cover indexes:', defaultLandCover.id);

            dispatch(setLandCoverIndex(defaultLandCover.id));
            dispatch(setProductivityLandCover(defaultLandCover.id));
            dispatch(setSoilOrganicIndex(defaultLandCover.id));
            dispatch(setSoilOrganicLandCoverIndex(defaultLandCover.id));
        }
    }, []); // Empty dependency array - runs only once

    // Initialize default years - runs only ONCE on mount
    useEffect(() => {
        console.log('[BaselineConfig] Initializing default years...');
        console.log('[BaselineConfig] Current years:', {
            productivity: { from: productivityYearFrom, to: productivityYearTo },
            landCover: { from: landCoverYearFrom, to: landCoverYearTo },
            soilOrganic: { from: soilOrganicYearFrom, to: soilOrganicYearTo }
        });

        // Set default productivity years
        if (!productivityYearFrom || !productivityYearTo) {
            console.log('[BaselineConfig] Setting default productivity years: 2001-2015');
            dispatch(setProductivityYears({
                from: 2001,
                to: 2015
            }));
        }

        // Set default land cover years
        if (!landCoverYearFrom || !landCoverYearTo) {
            console.log('[BaselineConfig] Setting default land cover years: 2001-2015');
            dispatch(setLandCoverYears({
                from: 2001,
                to: 2015
            }));
        }

        // Set default soil organic years
        if (!soilOrganicYearFrom || !soilOrganicYearTo) {
            console.log('[BaselineConfig] Setting default soil organic years: 2001-2015');
            dispatch(setSoilOrganicYears({
                from: 2001,
                to: 2015
            }));
        }
    }, []); // Empty dependency array - runs only once

    const productivityIndexes = allIndexes;
    const productivityDecimals = useSelector((state) => state.landDegradation.productivityDecimals);

    const [showAdvanced, setShowAdvanced] = useState(false);
    const productivityAdvanced = useSelector((state) => state.landDegradation.productivityAdvanced);

    useEffect(() => {
        dispatch(setAdvancedEnabled(showAdvanced));
        if (!showAdvanced) {
            dispatch(setProgressAdvancedEnabled(false));
        }
    }, [showAdvanced, dispatch]);

    const handleFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setProductivityAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMatrixChange = (key, values) => {
        // Parse and validate values
        const parsedValues = values.split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        // Check max 20 values
        if (parsedValues.length > 20) {
            toast.error("Maximum 20 values allowed");
            return;
        }

        const newMatrix = {
            ...productivityAdvanced.transformationMatrix,
            [key]: parsedValues
        };

        dispatch(setTransformationMatrix(newMatrix));
    };

    const handleCopyFromProgress = () => {
        const progressMatrix = store.getState().landDegradation.progressLandCoverIndicatorMatrix;
        if (progressMatrix) {
            dispatch(setLandCoverIndicatorMatrix(progressMatrix));
        } else {
            toast.warn("No progress matrix available to copy");
        }
    };

    const [showLandCoverAdvanced, setShowLandCoverAdvanced] = useState(false);
    const [showSoilOrganicAdvanced, setShowSoilOrganicAdvanced] = useState(false);
    const [showSoilOrganicLandCoverAdvanced, setShowSoilOrganicLandCoverAdvanced] = useState(false);
    const landCoverAdvanced = useSelector((state) => state.landDegradation.landCoverAdvanced);
    const soilOrganicAdvanced = useSelector((state) => state.landDegradation.soilOrganicAdvanced);
    const soilOrganicLandCoverAdvanced = useSelector((state) => state.landDegradation.soilOrganicLandCoverAdvanced);

    useEffect(() => {
        if (showLandCoverIndicatorMatrix) {
            if (!landCoverIndicatorMatrix) {
                dispatch(setLandCoverIndicatorMatrix(initialIndicatorMatrix));
            }
        } else {
            dispatch(setLandCoverIndicatorMatrix(null));
        }
    }, [showLandCoverIndicatorMatrix, dispatch]);

    useEffect(() => {
        dispatch(setLandCoverAdvancedEnabled(showLandCoverAdvanced));
        if (!showLandCoverAdvanced) {
            dispatch(setProgressLandCoverAdvancedEnabled(false));
        }
    }, [showLandCoverAdvanced, dispatch]);

    useEffect(() => {
        dispatch(setSoilOrganicAdvancedEnabled(showSoilOrganicAdvanced));
        if (!showSoilOrganicAdvanced) {
            dispatch(setProgressSoilOrganicAdvancedEnabled(false));
            // When SOC is disabled, also disable Land Cover upload
            setShowSoilOrganicLandCoverAdvanced(false);
        }
    }, [showSoilOrganicAdvanced, dispatch]);

    useEffect(() => {
        dispatch(setSoilOrganicLandCoverAdvancedEnabled(showSoilOrganicLandCoverAdvanced));
        if (!showSoilOrganicLandCoverAdvanced) {
            dispatch(setProgressSoilOrganicLandCoverAdvancedEnabled(false));
        }
    }, [showSoilOrganicLandCoverAdvanced, dispatch]);

    const handleLandCoverFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setLandCoverAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLandCoverMatrixChange = (key, values) => {
        const parsedValues = values.split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        if (parsedValues.length > 20) {
            toast.error("Maximum 20 values allowed");
            return;
        }

        const newMatrix = {
            ...landCoverAdvanced.transformationMatrix,
            [key]: parsedValues
        };
        dispatch(setLandCoverTransformationMatrix(newMatrix));
    };

    const handleSoilOrganicFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setSoilOrganicAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSoilOrganicLandCoverFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setSoilOrganicLandCoverAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSoilOrganicMatrixChange = (key, values) => {
        const parsedValues = values.split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        if (parsedValues.length > 20) {
            toast.error("Maximum 20 values allowed");
            return;
        }

        const newMatrix = {
            ...soilOrganicLandCoverAdvanced.transformationMatrix,
            [key]: parsedValues
        };
        dispatch(setSoilOrganicLandCoverTransformationMatrix(newMatrix));
    };

    return (
        <div className="land-degradation-config">
            <Typography className="config-title" variant="h5" gutterBottom>Land Degradation</Typography>
            <Typography className="config-title" variant="h6" gutterBottom>Baseline Period</Typography>

            {/* Productivity Index Section */}
            <div className="config-section">
                <Typography variant="h6" gutterBottom>
                    Productivity Index
                </Typography>
                <div className="input-group">
                    <Autocomplete
                        size="medium"
                        disablePortal={false}
                        options={productivityIndexes}
                        value={productivityIndex
                            ? allIndexes.find(index => index.id === productivityIndex)
                            : allIndexes.find(index => index.name.toLowerCase().includes("yearly ndvi")) || allIndexes[0]
                        }
                        getOptionLabel={(option) => option?.name || ""}
                        onChange={(_, value) => dispatch(setProductivityIndex(value?.id))}
                        renderInput={(params) => <TextField {...params} label="Select Productivity Index" />}
                    />
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            type="number"
                            label="Decimals"
                            defaultValue={1}
                            value={productivityDecimals}
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                // Allow empty string for typing
                                if (inputValue === '') {
                                    dispatch(setProductivityDecimals(''));
                                    return;
                                }
                                const value = parseInt(inputValue, 10);
                                // Only update if valid integer between 0-6
                                if (!isNaN(value) && value >= 0 && value <= 6) {
                                    dispatch(setProductivityDecimals(value));
                                }
                            }}
                            InputProps={{
                                inputProps: { min: 0 }
                            }}
                            size="medium"
                            sx={{ width: '300px' }}
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        {showAdvanced ? (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                    <TextField
                                        fullWidth
                                        type="file"
                                        label="First Land Cover"
                                        InputLabelProps={{ shrink: true }}
                                        onChange={handleFileUpload('firstLandCover')}
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        fullWidth
                                        type="file"
                                        label="Last Land Cover"
                                        InputLabelProps={{ shrink: true }}
                                        onChange={handleFileUpload('lastLandCover')}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button
                                        variant="text"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        startIcon={showAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        sx={{
                                            color: '#339ace',
                                            '&:hover': {
                                                backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                            }
                                        }}
                                    >
                                        Use From Portal's Land Cover
                                    </Button>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleOpenCopyDialog('productivity')}
                                        sx={{ color: '#339ace', borderColor: '#339ace' }}
                                    >
                                        Copy From
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleClearMatrix('productivity')}
                                        sx={{ color: '#339ace', borderColor: '#339ace' }}
                                    >
                                        Clear
                                    </Button>
                                </Box>

                                <Typography variant="subtitle2" gutterBottom>
                                    UNCCD Land Cover Transformation Matrix
                                </Typography>

                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>UNCCD Category</TableCell>
                                            <TableCell>Original Classes (comma-separated integers, max 20)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(productivityAdvanced.transformationMatrix).map(([key, value]) => (
                                            <TableRow key={key}>
                                                <TableCell>
                                                    <Typography variant="subtitle2" sx={{ color: '#339ace', fontWeight: 500 }}>
                                                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <MatrixRowInput
                                                        rowKey={key}
                                                        value={value}
                                                        onMatrixChange={(rowKey, parsedValues) => {
                                                            if (parsedValues.length > 20) {
                                                                toast.error("Maximum 20 values allowed");
                                                                return;
                                                            }
                                                            const newMatrix = {
                                                                ...productivityAdvanced.transformationMatrix,
                                                                [rowKey]: parsedValues
                                                            };
                                                            dispatch(setTransformationMatrix(newMatrix));
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Autocomplete
                                    size="medium"
                                    disablePortal={false}
                                    options={landCoverIndexes}
                                    value={productivityLandCover
                                        ? landCoverIndexes.find(index => index.id === productivityLandCover)
                                        : landCoverIndexes.find(index => index.name.toLowerCase().includes('esa cci')) || landCoverIndexes[0]
                                    }
                                    getOptionLabel={(option) => option?.name || ""}
                                    onChange={(_, value) => dispatch(setProductivityLandCover(value?.id))}
                                    renderInput={(params) => <TextField {...params} label="Select Land Cover" />}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    startIcon={showAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Upload Land Cover
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <Box className="year-picker-container">
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`productivity-from-${productivityLandCover}`}
                                setYear={(year) => dispatch(setProductivityYears({ from: year }))}
                                yearFrom={getIndexAvailableFrom(productivityLandCover)}
                                yearTo={getIndexAvailableTo(productivityLandCover)}
                                label="Year From"
                                defValue={productivityYearFrom || 2001}
                            />
                        </div>
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`productivity-to-${productivityLandCover}`}
                                setYear={(year) => dispatch(setProductivityYears({ to: year }))}
                                yearFrom={getIndexAvailableFrom(productivityLandCover)}
                                yearTo={getIndexAvailableTo(productivityLandCover)}
                                label="Year To"
                                defValue={productivityYearTo || 2015}
                            />
                        </div>
                    </Box>
                </div>
            </div>

            {/* Land Cover Section */}
            <div className="config-section">
                <Typography variant="h6" gutterBottom>
                    Land Cover
                </Typography>
                <div className="input-group">
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="text"
                            onClick={() => setShowLandCoverIndicatorMatrix(!showLandCoverIndicatorMatrix)}
                            startIcon={showLandCoverIndicatorMatrix ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            sx={{
                                color: '#339ace',
                                '&:hover': {
                                    backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                }
                            }}
                        >
                            {showLandCoverIndicatorMatrix ? 'Use Default Effects' : 'Define Effects of Land Cover Change'}
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, display: 'inline-flex' }}>
                            <InfoIcon sx={{ color: '#339ace', fontSize: '20px' }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                Optional
                            </Typography>
                        </Box>
                    </Box>

                    {showLandCoverIndicatorMatrix && (
                        <Box sx={{ mt: 2, mb: 3 }}>
                            <Box sx={{ display: 'flex', alignContent: 'center', gap: 3, justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', gap: 3, mb: 3, mt: 5, justifyContent: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 20, height: 20, bgcolor: '#4CAF50' }} />
                                        <Typography variant="body2">5 - Improvement</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 20, height: 20, bgcolor: '#FFFFFF', border: '1px solid #ccc' }} />
                                        <Typography variant="body2">4 - Stable</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 20, height: 20, bgcolor: '#9C27B0' }} />
                                        <Typography variant="body2">1 - Degradation</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3, mt: 5 }}>
                                    <Button variant='contained' onClick={handleCopyFromProgress} sx={{ color: '#fffff', ml: 2 }}>Copy from Progress</Button>
                                    <Button variant='contained' onClick={handleResetTable} sx={{ color: '#ffffff' }}>Reset Matrix</Button>
                                </Box>
                            </Box>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>From / To</TableCell>
                                        {Object.keys(landCoverIndicatorMatrix || initialIndicatorMatrix).map((key) => (
                                            <TableCell key={key} align="center">
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(landCoverIndicatorMatrix || initialIndicatorMatrix).map(([rowKey, rowValues]) => (
                                        <TableRow key={rowKey}>
                                            <TableCell>
                                                {rowKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </TableCell>
                                            {rowValues.map((value, colIndex) => (
                                                <TableCell key={colIndex} align="center" sx={{ p: 1 }}>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={value.toString()}
                                                        onChange={(e) => handleMatrixCellChange(rowKey, colIndex, e.target.value)}
                                                        sx={{
                                                            width: '100px',
                                                            '& .MuiInputBase-input': {
                                                                bgcolor: getTransitionColor(value.toString()),
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="1">1</MenuItem>
                                                        <MenuItem value="4">4</MenuItem>
                                                        <MenuItem value="5">5</MenuItem>
                                                    </TextField>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {showLandCoverAdvanced ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="First Land Cover"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleLandCoverFileUpload('firstLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="Last Land Cover"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleLandCoverFileUpload('lastLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowLandCoverAdvanced(!showLandCoverAdvanced)}
                                    startIcon={showLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Use From Portal's Land Cover
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleOpenCopyDialog('landcover')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Copy From
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleClearMatrix('landcover')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Clear
                                </Button>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>
                                UNCCD Land Cover Transformation Matrix
                            </Typography>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>UNCCD Category</TableCell>
                                        <TableCell>Original Classes (comma-separated integers, max 20)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(landCoverAdvanced.transformationMatrix).map(([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell>
                                                <Typography variant="subtitle2" sx={{ color: '#339ace', fontWeight: 500 }}>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <MatrixRowInput
                                                    rowKey={key}
                                                    value={value}
                                                    onMatrixChange={(rowKey, parsedValues) => {
                                                        if (parsedValues.length > 20) {
                                                            toast.error("Maximum 20 values allowed");
                                                            return;
                                                        }
                                                        const newMatrix = {
                                                            ...landCoverAdvanced.transformationMatrix,
                                                            [rowKey]: parsedValues
                                                        };
                                                        dispatch(setLandCoverTransformationMatrix(newMatrix));
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Autocomplete
                                size="medium"
                                disablePortal={false}
                                options={landCoverIndexes}
                                value={landCoverIndex
                                    ? landCoverIndexes.find(index => index.id === landCoverIndex)
                                    : landCoverIndexes.find(index => index.name.toLowerCase().includes('esa cci')) || landCoverIndexes[0]
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                onChange={(_, value) => dispatch(setLandCoverIndex(value?.id))}
                                renderInput={(params) => <TextField {...params} label="Select Land Cover Index" />}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                variant="text"
                                onClick={() => setShowLandCoverAdvanced(!showLandCoverAdvanced)}
                                startIcon={showLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                sx={{
                                    color: '#339ace',
                                    '&:hover': {
                                        backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                    }
                                }}
                            >
                                Upload Land Cover
                            </Button>
                        </Box>
                    )}
                    <Box className="year-picker-container">
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`landcover-from-${landCoverIndex}`}
                                setYear={(year) => dispatch(setLandCoverYears({ from: year }))}
                                yearFrom={getIndexAvailableFrom(landCoverIndex)}
                                yearTo={getIndexAvailableTo(landCoverIndex)}
                                label="Year From"
                                defValue={landCoverYearFrom || 2001}
                            />
                        </div>
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`landcover-to-${landCoverIndex}`}
                                setYear={(year) => dispatch(setLandCoverYears({ to: year }))}
                                yearFrom={getIndexAvailableFrom(landCoverIndex)}
                                yearTo={getIndexAvailableTo(landCoverIndex)}
                                label="Year To"
                                defValue={landCoverYearTo || 2015}
                            />
                        </div>
                    </Box>
                </div>
            </div>

            {/* Soil Organic Carbon Section */}
            <div className="config-section">
                <Typography variant="h6" gutterBottom>
                    Soil Organic Carbon
                </Typography>
                <div className="input-group">
                    {showSoilOrganicAdvanced ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="First Soil Organic Carbon Index (in tonnes per hectare)"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicFileUpload('firstSoilOrganic')}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="Last Soil Organic Carbon Index (in tonnes per hectare)"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicFileUpload('lastSoilOrganic')}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowSoilOrganicAdvanced(!showSoilOrganicAdvanced)}
                                    startIcon={showSoilOrganicAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button
                                variant="text"
                                onClick={() => setShowSoilOrganicAdvanced(!showSoilOrganicAdvanced)}
                                startIcon={showSoilOrganicAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                sx={{
                                    color: '#339ace',
                                    '&:hover': {
                                        backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                    }
                                }}
                            >
                                Upload Soil Organic Carbon Index
                            </Button>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InfoIcon sx={{ color: '#339ace', fontSize: '20px' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                    Optional
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <br />
                    {showSoilOrganicLandCoverAdvanced ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="First Land Cover Index"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicLandCoverFileUpload('firstSoilOrganicLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="Last Land Cover Index"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicLandCoverFileUpload('lastSoilOrganicLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowSoilOrganicLandCoverAdvanced(!showSoilOrganicLandCoverAdvanced)}
                                    startIcon={showSoilOrganicLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Use From Portal's Land Cover
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleOpenCopyDialog('soilorganic')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Copy From
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleClearMatrix('soilorganic')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Clear
                                </Button>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>
                                UNCCD Land Cover Transformation Matrix
                            </Typography>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>UNCCD Category</TableCell>
                                        <TableCell>Original Classes (comma-separated integers, max 20)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(soilOrganicLandCoverAdvanced.transformationMatrix).map(([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell>
                                                <Typography variant="subtitle2" sx={{ color: '#339ace', fontWeight: 500 }}>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <MatrixRowInput
                                                    rowKey={key}
                                                    value={value}
                                                    onMatrixChange={(rowKey, parsedValues) => {
                                                        if (parsedValues.length > 20) {
                                                            toast.error("Maximum 20 values allowed");
                                                            return;
                                                        }
                                                        const newMatrix = {
                                                            ...soilOrganicLandCoverAdvanced.transformationMatrix,
                                                            [rowKey]: parsedValues
                                                        };
                                                        dispatch(setSoilOrganicLandCoverTransformationMatrix(newMatrix));
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Autocomplete
                                size="medium"
                                disablePortal={false}
                                options={landCoverIndexes}
                                value={soilOrganicLandCoverIndex
                                    ? landCoverIndexes.find(index => index.id === soilOrganicLandCoverIndex)
                                    : landCoverIndexes.find(index => index.name.toLowerCase().includes('esa cci')) || landCoverIndexes[0]
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                onChange={(_, value) => dispatch(setSoilOrganicLandCoverIndex(value?.id))}
                                renderInput={(params) => <TextField {...params} label="Select Land Cover Index" />}
                                sx={{ flex: 1 }}
                            />
                            {showSoilOrganicAdvanced ? (
                                <Button
                                    variant="text"
                                    onClick={() => setShowSoilOrganicLandCoverAdvanced(!showSoilOrganicLandCoverAdvanced)}
                                    startIcon={showSoilOrganicLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Upload Land Cover
                                </Button>
                            ) : (
                                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                    Enable SOC upload to use custom Land Cover
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Box className="year-picker-container">
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`soilorganic-from-${soilOrganicLandCoverIndex}`}
                                setYear={(year) => dispatch(setSoilOrganicYears({ from: year }))}
                                yearFrom={getIndexAvailableFrom(soilOrganicLandCoverIndex)}
                                yearTo={getIndexAvailableTo(soilOrganicLandCoverIndex)}
                                label="Year From"
                                defValue={soilOrganicYearFrom || 2001}
                            />
                        </div>
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`soilorganic-to-${soilOrganicLandCoverIndex}`}
                                setYear={(year) => dispatch(setSoilOrganicYears({ to: year }))}
                                yearFrom={getIndexAvailableFrom(soilOrganicLandCoverIndex)}
                                yearTo={getIndexAvailableTo(soilOrganicLandCoverIndex)}
                                label="Year To"
                                defValue={soilOrganicYearTo || 2015}
                            />
                        </div>
                    </Box>
                </div>
            </div>

            {/* Copy From Dialog */}
            <Dialog open={copyFromDialogOpen} onClose={() => setCopyFromDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Copy Transformation Matrix From</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Select Source</InputLabel>
                        <Select
                            value={copySource}
                            onChange={(e) => setCopySource(e.target.value)}
                            label="Select Source"
                        >
                            <MenuItem value="baseline-productivity">Productivity Index (Baseline)</MenuItem>
                            <MenuItem value="baseline-landcover">Land Cover (Baseline)</MenuItem>
                            <MenuItem value="baseline-soilorganic">Soil Organic Carbon (Baseline)</MenuItem>
                            <MenuItem value="progress-productivity">Productivity Index (Progress)</MenuItem>
                            <MenuItem value="progress-landcover">Land Cover (Progress)</MenuItem>
                            <MenuItem value="progress-soilorganic">Soil Organic Carbon (Progress)</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                        <Button onClick={() => setCopyFromDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCopyMatrix}>Copy</Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default BaselineConfig;